import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Container,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { supabase } from '../lib/supabase';

const actionPlanStyles = {
  hero: {
    border: '1px solid #dbe4ee',
    borderRadius: '24px',
    overflow: 'hidden',
    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
    background: '#ffffff',
  },
  heroHeader: {
    background:
      'linear-gradient(135deg, #ffffff 0%, #f8fbfd 60%, #eef4f8 100%)',
    borderBottom: '1px solid #dbe4ee',
  },
  statCard: {
    border: '1px solid #dbe4ee',
    borderRadius: '20px',
    background: '#ffffff',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
  },
  panel: {
    border: '1px solid #dbe4ee',
    borderRadius: '22px',
    background: '#ffffff',
    boxShadow: '0 14px 32px rgba(15, 23, 42, 0.05)',
    overflow: 'hidden',
  },
  panelHeader: {
    background: '#f8fbfd',
    borderBottom: '1px solid #e2e8f0',
  },
  itemCard: {
    border: '1px solid #dbe4ee',
    borderRadius: '18px',
    background: '#fbfdff',
  },
  completedItemCard: {
    border: '1px solid #ccebd7',
    borderRadius: '18px',
    background: 'linear-gradient(180deg, #f6fcf8 0%, #f1faf4 100%)',
  },
  itemDescription: {
    borderLeft: '4px solid #bfdbfe',
    background: '#ffffff',
    borderRadius: '14px',
  },
  completedItemDescription: {
    borderLeft: '4px solid #86d4a3',
    background: '#ffffff',
    borderRadius: '14px',
  },
  itemPrimaryBlock: {
    border: '1px solid #dbe4ee',
    borderRadius: '14px',
    background: '#ffffff',
  },
  itemSecondaryBlock: {
    borderTop: '1px solid #e2e8f0',
    paddingTop: '0.9rem',
  },
  collapsedSummary: {
    borderLeft: '4px solid #bfdbfe',
    background: '#ffffff',
    borderRadius: '14px',
  },
  completedCollapsedSummary: {
    borderLeft: '4px solid #86d4a3',
    background: '#ffffff',
    borderRadius: '14px',
  },
} satisfies Record<string, React.CSSProperties>;

type ActionPlanRecord = {
  id: string;
  status: 'active' | 'completed' | 'archived' | null;
  created_at: string | null;
  overall_score_at_generation: number | null;
};

type ActionPlanItemRecord = {
  id: string;
  plan_id: string;
  title: string;
  description: string | null;
  priority: 'high' | 'medium' | 'low' | string | null;
  remediation_step: string | null;
  impacted_frameworks: string[] | null;
  related_section_titles: string[] | null;
  metadata: { estimated_hours?: number; category?: string } | null;
  is_completed?: boolean | null;
  completed_at?: string | null;
};

const ActionPlan = () => {
  const { planId } = useParams<{ planId: string }>();

  const [plan, setPlan] = useState<ActionPlanRecord | null>(null);
  const [items, setItems] = useState<ActionPlanItemRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completingItemId, setCompletingItemId] = useState<string | null>(null);
  const [confirmingItemId, setConfirmingItemId] = useState<string | null>(null);
  const [expandedItemIds, setExpandedItemIds] = useState<string[]>([]);

  const fetchActionPlan = useCallback(async () => {
    if (!planId) {
      setError('Missing action plan id.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const [planResponse, itemsResponse] = await Promise.all([
      supabase
        .from('action_plans')
        .select('id, status, created_at, overall_score_at_generation')
        .eq('id', planId)
        .single(),
      supabase
        .from('action_plan_items')
        .select(
          'id, plan_id, title, description, priority, remediation_step, impacted_frameworks, related_section_titles, metadata, is_completed, completed_at',
        )
        .eq('plan_id', planId),
    ]);

    if (planResponse.error) {
      setError(planResponse.error.message);
      setPlan(null);
      setItems([]);
      setLoading(false);
      return;
    }

    if (itemsResponse.error) {
      setError(itemsResponse.error.message);
      setPlan(planResponse.data as ActionPlanRecord);
      setItems([]);
      setLoading(false);
      return;
    }

    const priorityOrder: Record<string, number> = {
      high: 0,
      medium: 1,
      low: 2,
    };

    const sortedItems = ((itemsResponse.data ?? []) as ActionPlanItemRecord[])
      .slice()
      .sort((a, b) => {
        const left = priorityOrder[(a.priority ?? 'low').toLowerCase()] ?? 9;
        const right = priorityOrder[(b.priority ?? 'low').toLowerCase()] ?? 9;
        return left - right;
      });

    setPlan(planResponse.data as ActionPlanRecord);
    setItems(sortedItems);
    setLoading(false);
  }, [planId]);

  useEffect(() => {
    fetchActionPlan();
  }, [fetchActionPlan]);

  const totalEstimatedHours = useMemo(
    () =>
      items.reduce((acc, item) => {
        const hours = item.metadata?.estimated_hours ?? 0;
        return acc + (Number.isFinite(hours) ? hours : 0);
      }, 0),
    [items],
  );

  const completedItemsCount = useMemo(
    () => items.filter((item) => item.is_completed).length,
    [items],
  );

  const completedItemsPercentage = useMemo(() => {
    if (items.length === 0) return 0;
    return Math.round((completedItemsCount / items.length) * 100);
  }, [completedItemsCount, items.length]);

  const formatDate = (value: string | null) => {
    if (!value) return 'Unknown date';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown date';
    return date.toLocaleDateString();
  };

  const priorityVariant = (priority: string | null) => {
    if (!priority) return 'secondary';
    const p = priority.toLowerCase();
    if (p === 'high') return 'danger';
    if (p === 'medium') return 'warning';
    if (p === 'low') return 'success';
    return 'secondary';
  };

  const handleMarkCompleted = (itemId: string) => {
    const item = items.find((currentItem) => currentItem.id === itemId);
    if (!item || item.is_completed) {
      return;
    }

    setConfirmingItemId(itemId);
  };

  const toggleItemExpanded = (itemId: string) => {
    setExpandedItemIds((currentIds) =>
      currentIds.includes(itemId)
        ? currentIds.filter((currentId) => currentId !== itemId)
        : [...currentIds, itemId],
    );
  };

  const handleConfirmCompleted = async () => {
    if (!confirmingItemId) {
      return;
    }

    const item = items.find(
      (currentItem) => currentItem.id === confirmingItemId,
    );
    if (!item || item.is_completed) {
      setConfirmingItemId(null);
      return;
    }

    setCompletingItemId(confirmingItemId);

    const completedAt = new Date().toISOString();

    const { error: updateError } = await supabase
      .from('action_plan_items')
      .update({
        is_completed: true,
        completed_at: completedAt,
      })
      .eq('id', confirmingItemId);

    if (updateError) {
      setError(updateError.message);
      setCompletingItemId(null);
      setConfirmingItemId(null);
      return;
    }

    setItems((currentItems) =>
      currentItems.map((currentItem) =>
        currentItem.id === confirmingItemId
          ? {
              ...currentItem,
              is_completed: true,
              completed_at: completedAt,
            }
          : currentItem,
      ),
    );
    setCompletingItemId(null);
    setConfirmingItemId(null);
  };

  const confirmingItem =
    items.find((item) => item.id === confirmingItemId) ?? null;

  return (
    <Container fluid>
      <Card style={actionPlanStyles.hero} className="mb-4">
        <div style={actionPlanStyles.heroHeader} className="p-4 p-lg-5">
          <Row className="g-4 align-items-center">
            <Col lg={8}>
              <div
                className="text-uppercase fw-semibold mb-2"
                style={{
                  letterSpacing: '0.12em',
                  fontSize: '0.78rem',
                  color: '#64748b',
                }}
              >
                AI Remediation Workspace
              </div>
              <h2 className="mb-2">Action Plan</h2>
              <p className="text-muted mb-0">
                Review AI-generated remediation priorities, understand the
                reasoning behind each item, and plan the next compliance steps
                for your team.
              </p>
            </Col>
            <Col lg={4}>
              <div className="d-grid gap-2">
                <div
                  className="rounded-4 border p-3 bg-white"
                  style={{ borderColor: '#dbe4ee' }}
                >
                  <div className="d-flex align-items-center justify-content-between gap-3">
                    <div>
                      <div className="text-muted small mb-1">Created</div>
                      <div className="fw-semibold">
                        {formatDate(plan?.created_at ?? null)}
                      </div>
                    </div>
                    <div style={{ width: 64, height: 64 }}>
                      <CircularProgressbar
                        value={completedItemsPercentage}
                        text={`${completedItemsPercentage}%`}
                        styles={buildStyles({
                          textSize: '24px',
                          pathColor: '#198754',
                          textColor: '#0f172a',
                          trailColor: '#dbe4ee',
                        })}
                      />
                    </div>
                  </div>
                </div>
                <Link to="/my-compliance" className="btn btn-outline-secondary">
                  Back to My Compliance
                </Link>
              </div>
            </Col>
          </Row>
        </div>
      </Card>

      {loading ? (
        <div className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          <span>Loading action plan...</span>
        </div>
      ) : error ? (
        <Card className="border-danger">
          <Card.Body className="text-danger">
            Error loading action plan: {error}
          </Card.Body>
        </Card>
      ) : !plan ? (
        <Card style={actionPlanStyles.panel}>
          <Card.Body>Action plan not found.</Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-3 mb-4">
            <Col md={4}>
              <Card className="h-100" style={actionPlanStyles.statCard}>
                <Card.Body>
                  <div className="text-muted small mb-1">Status</div>
                  <div className="fw-semibold text-uppercase">
                    {plan.status ?? 'active'}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100" style={actionPlanStyles.statCard}>
                <Card.Body>
                  <div className="text-muted small mb-1">Steps Completed</div>
                  <div className="fw-semibold">
                    {completedItemsCount}/{items.length}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100" style={actionPlanStyles.statCard}>
                <Card.Body>
                  <div className="text-muted small mb-1">Estimated Effort</div>
                  <div className="fw-semibold">{totalEstimatedHours}h</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card style={actionPlanStyles.panel}>
            <div style={actionPlanStyles.panelHeader} className="p-3 p-md-4">
              <div className="fw-semibold mb-1">Remediation Items</div>
              <div className="text-muted small">
                {plan.overall_score_at_generation !== null
                  ? ` · Score at generation: ${plan.overall_score_at_generation}%`
                  : 'AI-generated item set based on your completed assessments.'}
              </div>
            </div>
            <Card.Body className="p-3 p-md-4">
              {items.length === 0 ? (
                <p className="mb-0 text-muted">
                  No remediation items were found for this plan.
                </p>
              ) : (
                <Row xs={1} className="g-3">
                  {items.map((item, idx) => (
                    <Col key={item.id}>
                      {(() => {
                        const isExpanded = expandedItemIds.includes(item.id);

                        return (
                          <Card
                            style={
                              item.is_completed
                                ? actionPlanStyles.completedItemCard
                                : actionPlanStyles.itemCard
                            }
                            className="h-100"
                          >
                            <Card.Body>
                              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
                                <div>
                                  <h4 className="mb-1">
                                    {idx + 1}. {item.title}
                                  </h4>
                                  <div
                                    className={`small fw-semibold ${
                                      item.is_completed
                                        ? 'text-success'
                                        : 'text-muted'
                                    }`}
                                  >
                                    {item.is_completed
                                      ? `Completed ${formatDate(item.completed_at ?? null)}`
                                      : 'In progress'}
                                  </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <Badge bg={priorityVariant(item.priority)}>
                                    {(item.priority ?? 'unknown').toUpperCase()}
                                  </Badge>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => toggleItemExpanded(item.id)}
                                    aria-expanded={isExpanded}
                                  >
                                    {isExpanded ? 'Collapse' : 'Expand'}
                                  </Button>
                                </div>
                              </div>

                              {item.description && (
                                <div
                                  className="px-3 py-3 mt-3 mb-3"
                                  style={
                                    item.is_completed
                                      ? actionPlanStyles.completedCollapsedSummary
                                      : actionPlanStyles.collapsedSummary
                                  }
                                >
                                  <div className="small text-uppercase text-muted fw-semibold mb-1">
                                    Summary
                                  </div>
                                  <div className="fs-6 lh-base">
                                    {item.description}
                                  </div>
                                </div>
                              )}

                              <Collapse in={isExpanded}>
                                <div>
                                  {item.remediation_step && (
                                    <div
                                      className="mt-2 p-3"
                                      style={actionPlanStyles.itemPrimaryBlock}
                                    >
                                      <div className="small text-uppercase text-muted fw-semibold mb-1">
                                        Remediation Step
                                      </div>
                                      <div>{item.remediation_step}</div>
                                    </div>
                                  )}

                                  <div className="mt-3">
                                    <Button
                                      variant={
                                        item.is_completed
                                          ? 'success'
                                          : 'primary'
                                      }
                                      disabled={
                                        item.is_completed ||
                                        completingItemId === item.id
                                      }
                                      onClick={() =>
                                        handleMarkCompleted(item.id)
                                      }
                                    >
                                      {item.is_completed
                                        ? 'Step completed'
                                        : completingItemId === item.id
                                          ? 'Marking complete...'
                                          : 'Mark as completed'}
                                    </Button>
                                  </div>

                                  <div
                                    className="mt-3 text-muted small"
                                    style={actionPlanStyles.itemSecondaryBlock}
                                  >
                                    {item.impacted_frameworks &&
                                      item.impacted_frameworks.length > 0 && (
                                        <div className="mt-2">
                                          <div className="small text-uppercase text-muted fw-semibold mb-1">
                                            Impacted Frameworks
                                          </div>
                                          <div>
                                            {item.impacted_frameworks.join(
                                              ', ',
                                            )}
                                          </div>
                                        </div>
                                      )}

                                    {item.related_section_titles &&
                                      item.related_section_titles.length >
                                        0 && (
                                        <div className="mt-3">
                                          <div className="small text-uppercase text-muted fw-semibold mb-1">
                                            Related Sections
                                          </div>
                                          <div>
                                            {item.related_section_titles.join(
                                              ', ',
                                            )}
                                          </div>
                                        </div>
                                      )}

                                    {item.metadata && (
                                      <div className="mt-3">
                                        {item.metadata.category && (
                                          <span>
                                            Category: {item.metadata.category}
                                          </span>
                                        )}
                                        {item.metadata.category &&
                                          item.metadata.estimated_hours !==
                                            undefined &&
                                          ' · '}
                                        {item.metadata.estimated_hours !==
                                          undefined && (
                                          <span>
                                            Estimated Hours:{' '}
                                            {item.metadata.estimated_hours}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </Collapse>
                            </Card.Body>
                          </Card>
                        );
                      })()}
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      <Modal
        show={!!confirmingItem}
        onHide={() => setConfirmingItemId(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Step Completion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Are you sure you have completed this step?</p>
          {confirmingItem && (
            <div className="small text-muted">{confirmingItem.title}</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setConfirmingItemId(null)}
            disabled={!!completingItemId}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConfirmCompleted}
            disabled={!!completingItemId}
          >
            {completingItemId ? 'Marking complete...' : 'Yes, mark completed'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ActionPlan;
